import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Download } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { installPendingApk, snoozeApkPrompt, subscribeApkPrompt } from '@/lib/appUpdate'

/**
 * Non-blocking prompt for a new app version on tablets that cannot install silently (Android ≤ 11 or
 * "Install unknown apps" not allowed). Only shown at quiet moments; any tap elsewhere hides it again.
 */
export function AppUpdateBanner() {
  const { t } = useTranslation('common')
  const [versionCode, setVersionCode] = useState<number | null>(null)

  useEffect(() => subscribeApkPrompt(setVersionCode), [])

  if (versionCode === null) return null

  return (
    <div
      data-app-update-banner
      role="status"
      className="fixed bottom-[max(1.25rem,env(safe-area-inset-bottom))] end-5 z-50 w-[22rem] max-w-[calc(100vw-2.5rem)] rounded-2xl border border-border/70 bg-card p-5 shadow-xl shadow-black/[0.08] animate-in fade-in slide-in-from-bottom-3 duration-300"
    >
      <div className="flex items-start gap-3.5">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-accent text-accent-foreground">
          <Download className="h-5 w-5" />
        </span>
        <div className="min-w-0 pt-0.5">
          <p className="font-display text-base font-semibold leading-tight">{t('appUpdateBanner.title')}</p>
          <p className="mt-1 text-pretty text-sm text-muted-foreground">{t('appUpdateBanner.text')}</p>
        </div>
      </div>
      <div className="mt-4 flex gap-2">
        <Button className="flex-1" onClick={() => void installPendingApk(true)}>
          {t('appUpdateBanner.install')}
        </Button>
        <Button variant="ghost" onClick={snoozeApkPrompt}>
          {t('appUpdateBanner.later')}
        </Button>
      </div>
    </div>
  )
}
